import { Fragment, useEffect, useMemo, useRef } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { SearchComponent } from 'components/SearchComponent/SearchComponent';
import { UpperDashboard } from 'sections/CustomerAccounts/UpperDashboard';
import { BottomTables } from 'sections/CustomerAccounts/BottomSection/index';
import { getCustomerDashboard, verifyCloseAccount } from 'services/customer-accounts.service';
import { useNavigate, useParams } from 'react-router-dom';
import {
	setCustomerDashboard,
	setError,
	getError as getCustomerAccountError,
	getCompanyDashboard,
	setGridTab,
} from 'state/reducers/customer-accounts.reducers';
import { CloseAccount } from 'sections/CustomerAccounts/CloseAccount';
import { getCloseAccountVerificationResult } from 'state/reducers/customer-accounts.reducers';
import { customerInitialAction } from 'constants/customer-account.constants';
import { WarningDialog } from 'components/WarningDialog';
import AccountsTable from 'sections/CustomerAccounts/accountsTable';
import { getAllCompaniesExpendedData } from 'services/companies.service';
import { pagesEnum } from 'constants/ui.constants';
type props = {
	initialAction?: string;
};

export const CustomerAccountsApp: React.FC<props> = (props: props) => {
	const searchPermission = hasPermission([permissionEnums.FREE_TEXT_SEARCH_DATABASE]);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const pagePermission = hasPermission([permissionEnums.CUSTOMER_ACCOUNTS]);
	const { id, initialAction } = useParams();
	const closeAccountVerificationResult = useAppSelector(getCloseAccountVerificationResult);
	const closeAccountPermission = hasPermission([permissionEnums.REMOVAL_ACCOUNT]);
	const customerAccountError = useAppSelector(getCustomerAccountError);
	const companyData = useAppSelector(getCompanyDashboard);
	const ref = useRef<HTMLDivElement>(null);

	const openDialog = useMemo(() => {
		return customerAccountError.exist;
	}, [customerAccountError]);

	const divider = (
		<Divider
			orientation="horizontal"
			flexItem
			sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
		/>
	);

	useEffect(() => {
		if (!pagePermission || !id) {
			dispatch(setCustomerDashboard(null));
		}
		if (pagePermission || !id) {
			dispatch(getAllCompaniesExpendedData());
		}
		if (pagePermission && id) {
			dispatch(getCustomerDashboard(parseInt(id)));
		}
	}, [pagePermission, id]);

	useEffect(() => {
		if (id && initialAction && companyData) {
			if (closeAccountPermission && initialAction === customerInitialAction.CLOSE_ACCOUNT) {
				dispatch(verifyCloseAccount(Number(id)));
			}
			if (closeAccountPermission && initialAction === customerInitialAction.WITHDRAWL_ACCOUNT) {
				//TODO: dispatch withdrawl action
			}
			if (initialAction === customerInitialAction.SUPPORT_REQUESTS && ref.current !== null) {
				setTimeout(() => {
					ref?.current?.scrollIntoView({ behavior: 'smooth' });
					dispatch(setGridTab(4));
					try {
						history.replaceState(null, '', `/${pagesEnum.CUSTOMER_ACCOUNTS}/${id}`);
					} catch (e) {}
				}, 200);
			}
		}
	}, [companyData]);

	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				subTitle={customerAccountError?.text}
				handleCancel={() => {
					dispatch(setError({ exist: false }));
				}}
			/>
			<Page
				title={t('page.customerAccounts.title')}
				subTitle={
					<Box>
						{searchPermission && (
							<Stack spacing={companyData ? 2 : 0}>
								<SearchComponent />
								{divider}
							</Stack>
						)}
					</Box>
				}
			>
				{pagePermission && (
					<Stack spacing={0}>
						{companyData && (
							<Stack spacing={4}>
								<UpperDashboard />
								{divider}
							</Stack>
						)}
						{companyData ? (
							<Stack spacing={4}>
								<BottomTables companyData={companyData} />
								{divider}
							</Stack>
						) : (
							<AccountsTable />
						)}
					</Stack>
				)}
				<Box ref={ref}></Box>
			</Page>
			{closeAccountVerificationResult && <CloseAccount companyId={Number(id)} />}
		</Fragment>
	);
};
